import React, { useMemo, useState, useEffect } from "react";
import { Link, useLocation, useHistory } from "react-router-dom";
import { TabView, TabPanel } from "primereact/tabview";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { InputText } from "primereact/inputtext";
import { Button } from "primereact/button";
import DashboardLayout from "../../layouts/DashboardLayout";
import DashboardSummaryStrip from "../../components/DashboardSummaryStrip/DashboardSummaryStrip";
import { useEconomicCircleData } from "./hooks/useEconomicCircleData";
import { useEconomicCircleTransactions } from "./hooks/useEconomicCircleData";
import useCurrency from "../../../../hooks/useCurrency";
import { formatPrice } from "../../../../utilities/formatPrice";
import { formatDate } from "../../../../utilities/formatDate";
import localeText from "../../../../locale";
import Loading from "../../../../components/Loading/Loading";
import "./EconomicCircleMembersPage.css";

const TAB_MEMBERS = "members";
const TAB_TRANSACTIONS = "transactions";

function getDefaultDateRange() {
  const end = new Date();
  const start = new Date();
  start.setDate(start.getDate() - 30);
  const fmt = (d) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  };
  return { dateBegin: fmt(start), dateEnd: fmt(end) };
}

function mapExtrabonusToRow(tx, index) {
  const amount = Number(tx.amount ?? tx.value ?? 0);
  const isCredit = amount >= 0;
  return {
    id: tx.id ?? index,
    description: tx.description ?? tx.type ?? "-",
    type: isCredit ? "credit" : "debit",
    value: Math.abs(amount),
    status: tx.status ?? "completed",
    statusKey: (tx.status || "completed").toLowerCase(),
    date: tx.date ?? tx.createdAt,
    _raw: tx,
  };
}

const EconomicCircleMembersPage = ({ userInfo, userTypeInfo }) => {
  const location = useLocation();
  const history = useHistory();
  const searchParams = new URLSearchParams(location.search || "");
  const tabParam = searchParams.get("tab");
  const validTab = tabParam === TAB_TRANSACTIONS ? TAB_TRANSACTIONS : TAB_MEMBERS;
  const [activeTabIndex, setActiveTabIndex] = useState(validTab === TAB_TRANSACTIONS ? 1 : 0);

  const {
    data,
    isLoading: dataLoading,
    error,
  } = useEconomicCircleData({ fetchTransactions: false });
  const { dateBegin, dateEnd } = getDefaultDateRange();
  const { transactions: rawTransactions, isLoading: txLoading } = useEconomicCircleTransactions(
    dateBegin,
    dateEnd,
  );
  const { currencySymbol: currency } = useCurrency(
    data?.balance?.currency || data?.rules?.currency,
  );

  useEffect(() => {
    const tab = searchParams.get("tab");
    const nextIndex = tab === TAB_TRANSACTIONS ? 1 : 0;
    if (nextIndex !== activeTabIndex) setActiveTabIndex(nextIndex);
  }, [location.search]);

  const setTabFromIndex = (index) => {
    setActiveTabIndex(index);
    const tab = index === 1 ? TAB_TRANSACTIONS : TAB_MEMBERS;
    const next = new URLSearchParams(location.search);
    next.set("tab", tab);
    history.replace(`${location.pathname}?${next.toString()}`);
  };

  const directs = data?.directs || [];
  const all = data?.all || [];
  const directUsernames = useMemo(
    () => new Set(directs.map((d) => d.nodeusername || d.node)),
    [directs],
  );
  const membersWithDirect = useMemo(
    () =>
      all.map((node) => ({
        ...node,
        isDirect: directUsernames.has(node.nodeusername || node.node),
      })),
    [all, directUsernames],
  );

  const [memberFilterLevel, setMemberFilterLevel] = useState("all");
  const [memberSearch, setMemberSearch] = useState("");
  const [txFilterType, setTxFilterType] = useState("all");
  const [txSearch, setTxSearch] = useState("");

  const filteredMembers = useMemo(() => {
    let list = [...membersWithDirect];
    if (memberFilterLevel === "direct") list = list.filter((m) => m.isDirect);
    if (memberFilterLevel === "indirect") list = list.filter((m) => !m.isDirect);
    if (memberSearch.trim()) {
      const q = memberSearch.toLowerCase();
      list = list.filter(
        (m) =>
          (m.node || "").toLowerCase().includes(q) ||
          (m.nodeusername || "").toLowerCase().includes(q),
      );
    }
    return list;
  }, [membersWithDirect, memberFilterLevel, memberSearch]);

  const transactions = useMemo(
    () => rawTransactions.map((t, i) => mapExtrabonusToRow(t, i)),
    [rawTransactions],
  );
  const filteredTransactions = useMemo(() => {
    let list = [...transactions];
    if (txFilterType === "credit") list = list.filter((t) => t.type === "credit");
    if (txFilterType === "debit") list = list.filter((t) => t.type === "debit");
    if (txSearch.trim()) {
      const q = txSearch.toLowerCase();
      list = list.filter(
        (t) => String(t.id).includes(q) || (t.description || "").toLowerCase().includes(q),
      );
    }
    return list;
  }, [transactions, txFilterType, txSearch]);

  const directCount = directs.length;
  const totalMembers = all.length;
  const indirectCount = Math.max(0, totalMembers - directCount);
  const activeInfluencePercent =
    totalMembers > 0 ? Math.round((directCount / totalMembers) * 100) : 0;
  const totalReceived = data?.balance?.totalReceived ?? data?.balance?.balance ?? 0;
  const withdrawals = data?.balance?.withdrawals ?? data?.balance?.withdrawn ?? 0;

  const breadcrumbLabel =
    activeTabIndex === 1
      ? localeText("transactions") || "Transações"
      : localeText("members") || "Membros";

  const statusConfig = {
    completed: { text: "completed", color: "var(--theme-primary, #1DAF61)" },
    paid: { text: "paid", color: "var(--theme-primary, #1DAF61)" },
    pending: { text: "pending", color: "#F7B005" },
    canceled: { text: "canceled", color: "#eb4034" },
    inactive: { text: "inactive", color: "#6b7280" },
    active: { text: "active", color: "var(--theme-primary, #1DAF61)" },
  };

  if (dataLoading && !data?.all) {
    return (
      <DashboardLayout>
        <div className="flex justify-content-center align-items-center min-h-20rem">
          <Loading classNameIcon="text-8xl" />
        </div>
      </DashboardLayout>
    );
  }

  if (error) {
    return (
      <DashboardLayout>
        <div className="flex justify-content-center align-items-center min-h-20rem">
          <p className="m-0" style={{ color: "var(--theme-text-muted)" }}>
            {localeText("managerDashboardErrorLoad") || "Erro ao carregar dados."}
          </p>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <nav
        className="flex align-items-center gap-2 mb-2 text-sm"
        style={{ color: "var(--theme-text-muted, #5C5C5C)" }}
        aria-label="Breadcrumb"
      >
        <Link to="/dashboard-economic-circle" style={{ color: "var(--theme-text-muted)" }}>
          {localeText("economicCircle") || "Círculo Econômico"}
        </Link>
        <span>/</span>
        <span style={{ color: "var(--theme-text, #171717)" }}>{breadcrumbLabel}</span>
      </nav>
      <div className="mb-4">
        <h1 className="text-xl font-semibold m-0" style={{ color: "var(--theme-text)" }}>
          {activeTabIndex === 0
            ? localeText("members") || "Membros"
            : localeText("transactions") || "Transações"}
        </h1>
        <p className="text-sm m-0 mt-1" style={{ color: "var(--theme-text-muted)" }}>
          {activeTabIndex === 0
            ? localeText("membersAndTransactionsSubtitle") || "Membros e transações do seu círculo."
            : localeText("transactionHistory") || "Histórico de transações de rede."}
        </p>
      </div>
      {dataLoading && (
        <div className="flex justify-content-center align-items-center min-h-20rem">
          <Loading classNameIcon="text-8xl" />
        </div>
      )}
      {activeTabIndex === 0 && !dataLoading && (
        <DashboardSummaryStrip
          items={[
            {
              label: localeText("directMembers") || "Membros diretos",
              value: directCount,
            },
            {
              label: localeText("indirectMembers") || "Membros indiretos",
              value: indirectCount,
            },
            {
              label: localeText("activeInfluence") || "Influência ativa",
              value: `${activeInfluencePercent}%`,
              valueColor: "var(--theme-primary)",
            },
          ]}
          className="economic-circle-summary"
        />
      )}
      {activeTabIndex === 1 && !dataLoading && (
        <DashboardSummaryStrip
          items={[
            {
              label: localeText("totalReceived") || "Total recebido",
              value: (
                <>
                  {currency} {formatPrice(totalReceived)}
                </>
              ),
              valueColor: "var(--theme-primary)",
            },
            {
              label: localeText("withdrawalsMade") || "Saques realizados",
              value: (
                <>
                  {currency} {formatPrice(withdrawals)}
                </>
              ),
            },
          ]}
          className="economic-circle-summary"
        />
      )}
      <TabView
        activeIndex={activeTabIndex}
        onTabChange={(e) => setTabFromIndex(e.index)}
        className="economic-circle-members-tabview"
      >
        <TabPanel header={localeText("members") || "Membros"}>
          <div className="flex flex-wrap align-items-center gap-2 mb-3 mt-3">
            <div
              className="flex gap-1 flex-wrap p-1"
              style={{
                backgroundColor: "color-mix(in srgb, var(--theme-border) 35%, transparent)",
                borderRadius: "0.625rem",
              }}
            >
              <Button
                size="small"
                label={localeText("all") || "Todos"}
                className={`p-button-text border-round-pill font-medium ${memberFilterLevel === "all" ? "economic-circle-filter-pill-active" : ""} p-no-outline`}
                style={{ color: "#A3A3A3" }}
                onClick={() => setMemberFilterLevel("all")}
              />
              <Button
                size="small"
                label={localeText("directs") || "Diretos"}
                className={`p-button-text border-round-pill font-medium ${memberFilterLevel === "direct" ? "economic-circle-filter-pill-active" : ""} p-no-outline`}
                style={{ color: "#A3A3A3" }}
                onClick={() => setMemberFilterLevel("direct")}
              />
              <Button
                size="small"
                label={localeText("indirects") || "Indiretos"}
                className={`p-button-text border-round-pill font-medium ${memberFilterLevel === "indirect" ? "economic-circle-filter-pill-active" : ""} p-no-outline`}
                style={{ color: "#A3A3A3" }}
                onClick={() => setMemberFilterLevel("indirect")}
              />
            </div>
            <span className="p-input-icon-left flex-1" style={{ minWidth: "200px" }}>
              <i className="pi pi-search" />
              <InputText
                value={memberSearch}
                onChange={(e) => setMemberSearch(e.target.value)}
                placeholder={localeText("searchByMember") || "Pesquise por membro"}
                className="w-full border-round-lg"
              />
            </span>
          </div>

          <div className="overflow-hidden">
            <DataTable
              value={filteredMembers}
              loading={dataLoading}
              paginator
              rows={10}
              rowsPerPageOptions={[10, 20, 50]}
              paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink"
              emptyMessage={
                <div className="flex flex-column align-items-center justify-content-center py-8 gap-3">
                  <i className="pi pi-users text-6xl text-400" />
                  <p className="m-0" style={{ color: "var(--theme-text-muted)" }}>
                    {localeText("noMembers") || "Nenhum membro."}
                  </p>
                </div>
              }
              className="economic-circle-members-datatable"
            >
              <Column
                header={localeText("name") || "Nome"}
                body={(row) => {
                  const name = row.node || row.nodeusername || "-";
                  const initials = (name + "?").slice(0, 2).toUpperCase();
                  return (
                    <div className="flex align-items-center gap-2">
                      <div
                        className="border-circle w-2rem h-2rem flex align-items-center justify-content-center text-xs font-medium"
                        style={{
                          backgroundColor: "var(--theme-border)",
                          color: "var(--theme-text)",
                        }}
                      >
                        {initials}
                      </div>
                      <span className="font-medium" style={{ color: "var(--theme-text)" }}>
                        {name}
                      </span>
                    </div>
                  );
                }}
              />
              <Column
                field="plan"
                header={localeText("type") || "Tipo"}
                body={(row) => (
                  <span
                    className="px-2 py-1 border-round text-xs"
                    style={{
                      backgroundColor: "color-mix(in srgb, var(--theme-border) 50%, transparent)",
                      color: "var(--theme-text)",
                    }}
                  >
                    {row.plan || "-"}
                  </span>
                )}
              />
              <Column
                field="level"
                header={localeText("network") || "Rede"}
                body={(row) => row.level ?? row.node ?? "-"}
              />
              <Column
                field="date"
                header={localeText("date") || "Data"}
                body={(row) => (row.date ? formatDate(row.date) : "-")}
              />
              <Column
                header={localeText("status") || "Status"}
                body={(row) => {
                  const status = row.status || (row.date ? "active" : "pending");
                  const key = status.toLowerCase();
                  const cfg = statusConfig[key] || statusConfig.pending;
                  return (
                    <span className="flex align-items-center gap-2">
                      <span
                        className="border-circle"
                        style={{ width: "8px", height: "8px", backgroundColor: cfg.color }}
                      />
                      {localeText(cfg.text) || status}
                    </span>
                  );
                }}
              />
              <Column
                header=""
                body={(row) => (
                  <Button
                    icon="pi pi-ellipsis-v"
                    className="p-button-text p-button-rounded p-button-plain"
                    aria-label={localeText("actions") || "Ações"}
                    onClick={(e) => {}}
                  />
                )}
                style={{ width: "56px" }}
              />
            </DataTable>
          </div>
        </TabPanel>

        <TabPanel header={localeText("transactions") || "Transações"}>
          <div className="flex flex-wrap align-items-center gap-2 mb-3 mt-3">
            <div
              className="flex gap-1 flex-wrap p-1"
              style={{
                backgroundColor: "color-mix(in srgb, var(--theme-border) 35%, transparent)",
                borderRadius: "0.625rem",
              }}
            >
              <Button
                size="small"
                label={localeText("all") || "Todas"}
                className={`p-button-text border-round-pill font-medium ${txFilterType === "all" ? "economic-circle-filter-pill-active" : ""} p-no-outline`}
                style={{ color: "#A3A3A3" }}
                onClick={() => setTxFilterType("all")}
              />
              <Button
                size="small"
                label={localeText("credit") || "Crédito"}
                className={`p-button-text border-round-pill font-medium ${txFilterType === "credit" ? "economic-circle-filter-pill-active" : ""} p-no-outline`}
                style={{ color: "#A3A3A3" }}
                onClick={() => setTxFilterType("credit")}
              />
              <Button
                size="small"
                label={localeText("debit") || "Débito"}
                className={`p-button-text border-round-pill font-medium ${txFilterType === "debit" ? "economic-circle-filter-pill-active" : ""} p-no-outline`}
                style={{ color: "#A3A3A3" }}
                onClick={() => setTxFilterType("debit")}
              />
            </div>
            <span className="p-input-icon-left flex-1" style={{ minWidth: "200px" }}>
              <i className="pi pi-search" />
              <InputText
                value={txSearch}
                onChange={(e) => setTxSearch(e.target.value)}
                placeholder={localeText("searchByTransaction") || "Pesquise por transação"}
                className="w-full border-round-lg"
              />
            </span>
            <Button
              label={localeText("filter") || "Filtrar"}
              icon="pi pi-sliders-h"
              className="p-button-outlined border-round-lg"
              style={{ color: "var(--theme-text-muted)", borderColor: "var(--theme-border)" }}
            />
          </div>

          <div className="overflow-hidden">
            <DataTable
              value={filteredTransactions}
              loading={txLoading}
              paginator
              rows={10}
              rowsPerPageOptions={[10, 20, 50]}
              paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink"
              emptyMessage={
                <div className="flex flex-column align-items-center justify-content-center py-8 gap-3">
                  <i className="pi pi-inbox text-6xl text-400" />
                  <p className="m-0" style={{ color: "var(--theme-text-muted)" }}>
                    {localeText("noOperationsRecords") || "Nenhum registro de operação."}
                  </p>
                </div>
              }
              className="economic-circle-members-datatable"
            >
              <Column
                field="id"
                header={localeText("transaction") || "Transação"}
                body={(row) => <span className="font-medium">{row.id}</span>}
              />
              <Column field="description" header={localeText("description") || "Descrição"} />
              <Column
                field="type"
                header={localeText("operationType") || "Tipo de operação"}
                body={(row) => (
                  <span
                    className="px-2 py-1 border-round text-xs font-medium"
                    style={{
                      color: row.type === "credit" ? "var(--theme-primary)" : "#eb4034",
                      backgroundColor:
                        row.type === "credit" ? "rgba(29,175,97,0.15)" : "rgba(235,64,52,0.1)",
                    }}
                  >
                    {row.type === "credit"
                      ? localeText("credit") || "Crédito"
                      : localeText("debit") || "Débito"}
                  </span>
                )}
              />
              <Column
                field="value"
                header={localeText("value") || "Valor"}
                body={(row) => (
                  <span className="font-medium">
                    {row.type === "credit" ? "+" : "-"} {currency} {formatPrice(row.value)}
                  </span>
                )}
              />
              <Column
                field="statusKey"
                header={localeText("status") || "Status"}
                body={(row) => {
                  const cfg = statusConfig[row.statusKey] || statusConfig.completed;
                  return (
                    <span className="flex align-items-center gap-2">
                      <span
                        className="border-circle"
                        style={{ width: "8px", height: "8px", backgroundColor: cfg.color }}
                      />
                      {localeText(cfg.text) || row.status}
                    </span>
                  );
                }}
              />
              <Column
                field="date"
                header={localeText("date") || "Data"}
                body={(row) => formatDate(row.date)}
              />
            </DataTable>
          </div>
        </TabPanel>
      </TabView>
    </DashboardLayout>
  );
};

export default EconomicCircleMembersPage;
